// Decides which invoicing sub-table the expanded row draws for a given company, and the
// column and row vocabulary all three share.
//
// The presentation twin of Payments::PerSeatInvoicingBreakdown and
// Payments::InvoicingClassBreakdown on the Ruby side, and deliberately the same shape as
// companies/tabs/pricingSurface.js: a pure module, no React, no network, no form state. That
// keeps the "which table" rule testable without rendering anything.
//
// It returns a KIND rather than a component. Returning a component would mean importing the
// three tables here, which would drag React into the one file that has no business knowing
// about it — so InvoicingTable maps the kind onto a component instead.

// The nine bands, in render order. Mirrors PerSeatBilling::DistanceBand and the band keys
// Payments::InvoicingClassBreakdown puts on the payload — a boundary moving changes all of
// them together.
export const KM_BANDS = [
  { key: 'ten', label: '0-19 KMs' },
  { key: 'twenty', label: '20-29 KMs' },
  { key: 'thirty', label: '30-39 KMs' },
  { key: 'forty', label: '40-49 KMs' },
  { key: 'fifty', label: '50-59 KMs' },
  { key: 'sixty', label: '60-69 KMs' },
  { key: 'seventy', label: '70-79 KMs' },
  { key: 'eighty', label: '80-89 KMs' },
  { key: 'ninety_plus', label: '90- KMs' },
];

// Legacy only: the call-out quantity, then the two money columns. Both fees are ex-VAT —
// Xero owns VAT. A nil fee means the company has no rate card for that class and renders as
// a dash, never as R 0.00.
export const LEGACY_COUNT_COLUMNS = [{ key: 'cancellations', label: 'Call-Outs' }];
export const LEGACY_FEE_COLUMNS = [
  { key: 'call_out_fee', label: 'Call-Out Fee' },
  { key: 'service_fee', label: 'Service Fee' },
];

// The four per-seat rows, in the order both per-seat tables render them. The first three
// count BOOKINGS and partition them; call_outs counts RIDES. Two units in one table, which
// is why neither table offers a column total — reading down would add bookings to rides.
export const PER_SEAT_ROWS = [
  { key: 'rate_per_booking', label: 'Rate Per Booking' },
  { key: 'single_trip', label: 'Single Trip' },
  { key: 'ad_hoc_trip', label: 'Ad Hoc Trip' },
  { key: 'call_outs', label: 'Call-Outs' },
];

// The three booking categories, which are the Fixed table's columns. call_outs is excluded
// on purpose: it is a column there too, but it counts rides and is summed separately.
export const PER_SEAT_CATEGORIES = PER_SEAT_ROWS.filter((row) => row.key !== 'call_outs');

const KIND_BY_BILLING_MODEL = {
  per_seat_tiered: 'tiered',
  per_seat_fixed: 'fixed',
};

/**
 * Which sub-table an invoicing row should draw.
 *
 * Routes on `billing_model` ALONE, and that is deliberate. The presence of
 * `toggle_table.per_seat` is not consulted as a second condition because it cannot disagree:
 * Payments::WeeklyOverviewReport builds both off the same Company record in the same pass,
 * computing the breakdown exactly when `company.per_seat_billing?` — the same two models
 * KIND_BY_BILLING_MODEL keys on. One payload, one answer.
 *
 * Falls back to 'legacy' only for an unrecognised or missing billing model, mirroring
 * Payments::InvoicingCsv's own fallback.
 *
 * A per-seat row arriving without its breakdown is therefore a broken backend contract
 * rather than a state to route around, and it is NOT sent to legacy: the vehicle matrix is
 * the wrong document for a per-seat company, and quietly drawing it would misreport an
 * invoice. The per-seat tables each tolerate the gap themselves — see TieredPerSeatTable's
 * `perSeat?.[row.key] || {}` and FixedPerSeatTable's `rowTotal(perSeat?.[...])` — so such a
 * row renders its own table, zeroed, which reads as "nothing counted" instead of as someone
 * else's numbers.
 *
 * @param {Object} item - The company's invoicing row from weekly_summary.
 * @returns {'legacy'|'fixed'|'tiered'}
 */
export const invoicingSubTableKindFor = (item = {}) =>
  KIND_BY_BILLING_MODEL[item.billing_model] ?? 'legacy';

/**
 * One per-seat row's nine band counts added up. A ROW total only — see PER_SEAT_ROWS.
 *
 * @param {Object} row - One breakdown row, keyed by band.
 * @returns {number}
 */
export const rowTotal = (row = {}) => KM_BANDS.reduce((sum, band) => sum + (row[band.key] ?? 0), 0);
